import {
  getGetApiRolePermissionQueryKey,
  Permission,
  Role,
  RolePermissionDto,
  useGetApiRolePermission,
  usePutApiRolePermission,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { permissions, roles } from "@/utils/rolesAndPermissions";
import { _permissionsDescriptionMap } from "@/utils/rolesAndPermissionsData/permissionsDescriptionMap";
import { _permissionsGroupMap } from "@/utils/rolesAndPermissionsData/permissionsGroupMap";
import { _rolesNameMap } from "@/utils/rolesAndPermissionsData/rolesNameMap";
import {
  Checkbox,
  InputAdornment,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQueryClient } from "@tanstack/react-query";
import { orderBy, uniq } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import Loading from "../Loading";
import Button from "@/elements/Button";
import { _permissionsNameMap } from "@/utils/rolesAndPermissionsData/permissionsNameMap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const gruppi = orderBy(uniq(Object.values(_permissionsGroupMap)));

type RolesPermissionMap = Record<`${Role}_${Permission}`, boolean>;

function serializeToDto(
  permissionsMap: RolesPermissionMap
): RolePermissionDto[] {
  const result: RolePermissionDto[] = [];
  for (const role of roles) {
    if (role === "Admin") {
      continue;
    }

    for (const permission of permissions) {
      const key = `${role}_${permission}` as const;
      if (permissionsMap[key]) {
        result.push({
          role: role,
          permission: permission,
        });
      }
    }
  }

  return result;
}

function RuoliPermessiPage() {
  const { t } = useTranslation();

  const { hasPermission } = useAuth();
  const canEditPermissions = hasPermission("RuoliPermessiUpdate");

  const queryClient = useQueryClient();
  const { isLoading, data, error } = useGetApiRolePermission();
  const putMutation = usePutApiRolePermission();

  const [ruoliPermessi, setRuoliPermessi] = useState<RolesPermissionMap>();
  const [originalRuoliPermessi, setOriginalRuoliPermessi] =
    useState<RolesPermissionMap>();
  const [search, setSearch] = useState("");

  const hasChanges = useMemo(() => {
    if (!ruoliPermessi || !originalRuoliPermessi) {
      return false;
    }

    const currentDto = serializeToDto(ruoliPermessi);
    const originalDto = serializeToDto(originalRuoliPermessi);

    if (currentDto.length !== originalDto.length) {
      return true;
    }

    return !currentDto.every((current) =>
      originalDto.some(
        (original) =>
          original.role === current.role &&
          original.permission === current.permission
      )
    );
  }, [ruoliPermessi, originalRuoliPermessi]);

  const handleSave = async () => {
    if (!ruoliPermessi) {
      return;
    }

    try {
      const dto = serializeToDto(ruoliPermessi);
      await putMutation.mutateAsync({ data: dto });

      await queryClient.invalidateQueries({
        queryKey: getGetApiRolePermissionQueryKey(),
      });

      toast.success(t("ruoliPermessiPage.toast.salvati"));
    } catch (error) {
      console.error(t("ruoliPermessiPage.errori.salvataggioConsole"), error);
      toast.error(t("ruoliPermessiPage.errori.salvataggio"));
    }
  };

  useEffect(() => {
    const ruoliPermessiList = data?.data;
    if (ruoliPermessiList) {
      const ruoliPermessiMap: Partial<
        Record<`${Role}_${Permission}`, boolean>
      > = {};
      for (const role of roles) {
        for (const permission of permissions) {
          const key = `${role}_${permission}` as const;
          const hasPermission = ruoliPermessiList.some(
            (rp) => rp.role === role && rp.permission === permission
          );
          ruoliPermessiMap[key] = hasPermission;
        }
      }
      const typedMap = ruoliPermessiMap as RolesPermissionMap;
      setRuoliPermessi(typedMap);
      setOriginalRuoliPermessi(typedMap);
    }
  }, [data?.data]);

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h4" mr={2}>
          {t("ruoliPermessiPage.titolo")}
        </Typography>

        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            size="small"
            placeholder={t("ruoliPermessiPage.filtri.cerca")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {canEditPermissions && (
            <Button
              onClick={handleSave}
              disabled={!hasChanges || putMutation.isLoading}
            >
              {t("ruoliPermessiPage.azioni.salvaModifiche")}
            </Button>
          )}
        </Box>
      </Box>
      {isLoading && <Loading />}
      {error && (
        <Typography>{t("ruoliPermessiPage.errori.caricamento")}</Typography>
      )}

      {ruoliPermessi && (
        <TableContainer
          sx={{
            mt: 2,
            minWidth: 0,
            width: "auto",
          }}
        >
          <MuiTable>
            <Box component="thead">
              <TableRow>
                <TableCell>{t("ruoliPermessiPage.colonne.permesso")}</TableCell>

                {roles.map((r) => {
                  return <TableCell key={r}>{_rolesNameMap[r]}</TableCell>;
                })}
              </TableRow>
            </Box>

            <TableBody>
              {gruppi.map((g) => {
                const permessi = orderBy(
                  Object.entries(_permissionsGroupMap)
                    .filter(([, group]) => group === g)
                    .map(([permesso]) => permesso)
                ) as (keyof typeof _permissionsGroupMap)[];

                const query = search.trim().toLowerCase();
                const permessiFiltrati = query
                  ? permessi.filter(
                      (p) =>
                        g.toLowerCase().includes(query) ||
                        (_permissionsNameMap[p] as string)?.toLowerCase().includes(query) ||
                        (_permissionsDescriptionMap[p] as string)?.toLowerCase().includes(query)
                    )
                  : permessi;

                if (permessiFiltrati.length === 0) return null;

                return (
                  <Fragment key={g}>
                    <TableRow>
                      <TableCell colSpan={1 + roles.length}>
                        <strong>{g}</strong>
                      </TableCell>
                    </TableRow>

                    {permessiFiltrati.map((p) => {
                      return (
                        <TableRow key={p}>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <strong>{_permissionsNameMap[p]}</strong>
                              <Typography variant="subtitle2">
                                {_permissionsDescriptionMap[p]}
                              </Typography>
                            </Box>
                          </TableCell>

                          {roles.map((r) => {
                            const hasPermission =
                              r === "Admin" || ruoliPermessi[`${r}_${p}`];
                            return (
                              <TableCell key={r}>
                                <Checkbox
                                  disabled={
                                    r === "Admin" ||
                                    !canEditPermissions ||
                                    putMutation.isLoading
                                  }
                                  checked={hasPermission}
                                  onChange={(e) => {
                                    if (canEditPermissions) {
                                      setRuoliPermessi(
                                        (prev) =>
                                          ({
                                            ...(prev || {}),
                                            [`${r}_${p}`]: e.target.checked,
                                          } as Record<
                                            `${Role}_${Permission}`,
                                            boolean
                                          >)
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      )}
    </Box>
  );
}

export default RuoliPermessiPage;
